import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Loader2, RefreshCw } from "lucide-react";

import {
  buildStructuredVisualStyle,
  getStructuredVisualStyle,
} from "../../lib/brandStudioApi";

const SECTION_LABELS = {
  color_direction: "Цвет",
  photography_language: "Фотография",
  material_language: "Материалы / фактуры",
  graphic_language: "Графический язык",
  composition: "Композиция",
};

const SIGNAL_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  composition: "Композиция",
  photography: "Фотография",
  graphics: "Графика",
  packaging: "Упаковка",
  material_texture: "Материал / фактура",
  mood: "Настроение",
  logo_mark: "Логотип / знак",
  brandbook_presentation: "Подача брендбука",
  whole: "Всё целиком",
  other: "Другое",
};

const NEED_LABELS = {
  identity: "Фирменный стиль",
  social: "Соцсети",
  business: "Для бизнеса",
  client: "Для клиента",
  print_ads: "Печать и реклама",
  packaging_physical: "Упаковка",
  digital: "Цифровые материалы",
  website: "Сайт",
  custom: "Своя задача",
};

const ROLE_LABELS = {
  signature: "Фирменное имя / подпись",
  headline: "Заголовки",
  body: "Основной текст",
  accent: "Акцент",
  decorative: "Декоративный",
  character: "Характер",
  character: "Характер",
};

const LOGO_LABELS = {
  existing: "Есть существующий логотип",
  need_new: "Нужен новый логотип",
  not_needed: "Логотип пока не нужен",
  unset: "Решение не задано",
};

const STATUS_LABELS = {
  current: "текущая",
  superseded: "предыдущая",
};

const tierLabel = (value) => {
  const map = {
    user_explicit: "явное решение пользователя",
    user_confirmed: "подтверждено пользователем",
    repeated_aligned_evidence: "повторяющееся согласованное наблюдение",
    system_inferred: "системное предположение",
    weak_visual_occurrence: "слабое визуальное наблюдение",
  };
  return map[value] || value;
};

const sourceTypeLabel = (value) => {
  const map = {
    brand_story: "рассказ пользователя",
    confirmed_understanding: "подтверждённое понимание",
    business_data: "данные бизнеса",
    needs: "задачи бренда",
    reference_user_input: "отметки / заметка референса",
    reference_image: "визуальное наблюдение референса",
    typography: "типографический ориентир",
    logo: "решение по логотипу",
    explicit_visual_preference: "явное визуальное решение",
  };
  return map[value] || value;
};

function CompactDecisionList({ items = [], max = 4, empty = "Нет." }) {
  if (!items.length) return <div className="text-[11px] text-[#8A745E]">{empty}</div>;
  const visible = items.slice(0, max);
  return (
    <div className="space-y-1.5">
      {visible.map((item) => (
        <div key={item.id} className="text-[11.5px] leading-5 text-[#4C392B]">
          • {item.text}
        </div>
      ))}
      {items.length > max && (
        <div className="text-[10px] text-[#927A5B]">
          + ещё {items.length - max} наблюдений — в деталях
        </div>
      )}
    </div>
  );
}

function Provenance({ items = [] }) {
  if (!items.length) {
    return <div className="text-[9.5px] text-[#9A8871]">Основание не указано.</div>;
  }
  return (
    <div className="space-y-1">
      {items.map((item, index) => (
        <div key={`${item.source_type}-${item.source_id}-${index}`} className="text-[9.5px] leading-4 text-[#8A745E]">
          {sourceTypeLabel(item.source_type)} · {tierLabel(item.tier)}
          {item.fields?.length ? ` · поля: ${item.fields.join(", ")}` : ""}
        </div>
      ))}
    </div>
  );
}

function FullDecisionList({ items = [] }) {
  if (!items.length) return <div className="text-[11px] text-[#8A745E]">Нет данных.</div>;
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.id} className="border-b border-[#EEE4D6] pb-2 last:border-b-0">
          <div className="text-[11.5px] leading-5 text-[#4C392B]">• {item.text}</div>
          <div className="mt-1 text-[9.5px] text-[#8A745E]">
            {tierLabel(item.strength)}
            {" · "}референсов: {item.reference_ids?.length || 0}
            {item.locked ? " · зафиксировано" : " · рекомендательное"}
          </div>
          <div className="mt-1">
            <Provenance items={item.provenance || []} />
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailsBlock({ title, subtitle, children, testid }) {
  return (
    <details
      className="rounded-[14px] border border-[#DED0BC] bg-white"
      data-testid={testid}
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3">
        <div>
          <div className="text-[11.5px] font-medium text-[#4C392B]">{title}</div>
          {subtitle && <div className="mt-0.5 text-[10px] text-[#8A745E]">{subtitle}</div>}
        </div>
        <ChevronDown size={15} className="shrink-0 text-[#927A5B]" />
      </summary>
      <div className="border-t border-[#EEE4D6] px-4 py-4">{children}</div>
    </details>
  );
}

export default function StructuredVisualStyleOwnerPanelV1() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [missing, setMissing] = useState([]);

  const load = async () => {
    setStatus("loading");
    setMessage("");
    try {
      setData(await getStructuredVisualStyle());
      setStatus("ready");
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      setMessage(
        (typeof detail === "object" && detail?.code) ||
          (typeof detail === "string" && detail) ||
          "Не удалось загрузить структурированный визуальный стиль.",
      );
      setStatus("error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const build = async () => {
    setStatus("building");
    setMessage("");
    setMissing([]);
    try {
      const result = await buildStructuredVisualStyle();
      if (result.result === "not_ready") {
        setMissing(result.missing_semantic_groups || []);
        setStatus("not_ready");
        return;
      }
      setData(result.visual_style || null);
      setMessage(
        result.result === "reused"
          ? "Исходные данные не изменились — сохранённая версия переиспользована."
          : "Создана новая версия структурированного визуального стиля.",
      );
      setStatus("ready");
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      setMessage(
        (typeof detail === "object" && detail?.code) ||
          (typeof detail === "string" && detail) ||
          "Не удалось собрать структурированный визуальный стиль.",
      );
      setStatus("error");
    }
  };

  const sections = useMemo(() => {
    if (!data) return [];
    return Object.entries(SECTION_LABELS).map(([key, label]) => ({
      key,
      label,
      items: data[key]?.items || [],
    }));
  }, [data]);

  const typography = data?.typography_character || {};
  const references = data?.reference_context || [];
  const applications = data?.applications_context || {};

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="structured-visual-style-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · VS1.2
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Структурированный визуальный стиль
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Короткое резюме того, что Brand Studio уже поняла и сохранила. Технические
        основания и полный массив наблюдений спрятаны в раскрывающихся деталях.
      </p>

      <button
        type="button"
        onClick={build}
        disabled={status === "building"}
        className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#3D2A20] px-5 text-[12px] text-[#FFF8ED] disabled:opacity-50"
        data-testid="structured-visual-style-build-v1"
      >
        {status === "building" ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Собираю…
          </>
        ) : (
          <>
            <RefreshCw size={14} />
            Проверить сборку
          </>
        )}
      </button>

      {status === "loading" && (
        <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Проверяю текущую версию…
        </div>
      )}

      {status === "not_ready" && (
        <div className="mt-4 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">
          Сначала должны быть готовы уже сохранённые семантические группы VU5.2.
          {missing.length > 0 && <div className="mt-2">Не готовы: {missing.join(", ")}</div>}
        </div>
      )}

      {message && (
        <div className="mt-4 rounded-[13px] border border-[#DED0BC] bg-white px-4 py-3 text-[12px] leading-5 text-[#655141]">
          {message}
        </div>
      )}

      {data && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> Версия {data.version_number} · {STATUS_LABELS[data.status] || data.status}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              20/20 референсов сохранены
              {" · "}{data.build_report?.semantic_pattern_included || 0}/
              {data.build_report?.semantic_pattern_total || 0} смысловых паттернов
              {" · "}ИИ-вызовов: {data.build_report?.provider_call_count || 0}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Характер визуального мира
              </div>
              <div className="mt-2">
                <CompactDecisionList items={data.setting?.items || []} max={2} />
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Где бренд должен работать
              </div>
              <div className="mt-2 text-[11.5px] leading-5 text-[#4C392B]">
                {(applications.needs || []).map((item) => NEED_LABELS[item] || item).join(" · ") || "—"}
              </div>
            </article>
          </div>

          {(data.rules_boundaries?.explicit_decisions || []).length > 0 && (
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Явные решения пользователя · зафиксировано
              </div>
              <div className="mt-2">
                <CompactDecisionList items={data.rules_boundaries.explicit_decisions} max={6} />
              </div>
            </article>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Что важно сохранить
              </div>
              <div className="mt-2">
                <CompactDecisionList items={data.rules_boundaries?.preserve || []} max={2} />
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Чего избегать
              </div>
              <div className="mt-2">
                <CompactDecisionList items={data.avoid?.items || []} max={2} />
              </div>
            </article>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {sections.map((section) => (
              <article key={section.key} className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                    {section.label}
                  </div>
                  <div className="text-[9.5px] text-[#9A8871]">
                    {section.items.length} наблюдений
                  </div>
                </div>
                <div className="mt-2">
                  <CompactDecisionList items={section.items} max={3} />
                </div>
              </article>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Типографический ориентир
              </div>
              <div className="mt-2 text-[11px] leading-5 text-[#655141]">
                {(typography.preferences || []).length ? (
                  typography.preferences
                    .map((item) => item.font_family || item.font_catalog_id || item.font_original_filename || ROLE_LABELS[item.role] || item.role)
                    .join(" · ")
                ) : typography.legacy_anchor ? (
                  <>
                    {typography.legacy_anchor.mode === "provided"
                      ? "Основной визуальный ориентир сохранён."
                      : "Сохранено решение: Brand Studio должна предложить типографические варианты."}
                    {!!typography.legacy_anchor.roles?.length && (
                      <>
                        <br />
                        Роли: {typography.legacy_anchor.roles.map((item) => ROLE_LABELS[item] || item).join(" · ")}
                      </>
                    )}
                    {typography.legacy_anchor.note ? (
                      <>
                        <br />
                        Заметка: {typography.legacy_anchor.note}
                      </>
                    ) : null}
                  </>
                ) : (
                  "Активных ориентиров нет."
                )}
              </div>
              <div className="mt-1 text-[9.5px] text-[#8A745E]">
                Финальная типографическая система ещё не создаётся.
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">
                Решение по логотипу
              </div>
              <div className="mt-2 text-[11.5px] leading-5 text-[#4C392B]">
                {LOGO_LABELS[data.logo_context?.status] || data.logo_context?.status || "—"}
              </div>
            </article>
          </div>

          <div className="space-y-2 pt-1">
            <DetailsBlock
              title="Полные наблюдения по визуальным направлениям"
              subtitle="Смысловые паттерны и основания каждого решения"
              testid="vs12-details-patterns"
            >
              <div className="grid gap-4 md:grid-cols-2">
                {sections.map((section) => (
                  <div key={section.key}>
                    <div className="mb-2 text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      {section.label}
                    </div>
                    <FullDecisionList items={section.items} />
                  </div>
                ))}
                {(typography.semantic_items || []).length > 0 && (
                  <div>
                    <div className="mb-2 text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      Типографика
                    </div>
                    <FullDecisionList items={typography.semantic_items} />
                  </div>
                )}
                {(applications.semantic_items || []).length > 0 && (
                  <div>
                    <div className="mb-2 text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      Применения
                    </div>
                    <FullDecisionList items={applications.semantic_items} />
                  </div>
                )}
              </div>
            </DetailsBlock>

            <DetailsBlock
              title="Все 20 референсов и пользовательские роли"
              subtitle="Отметки и заметки пользователя без отбора «лучших восьми»"
              testid="vs12-details-references"
            >
              <div className="grid gap-3 md:grid-cols-2">
                {references.map((reference) => (
                  <div
                    key={reference.reference_id}
                    className="rounded-[12px] border border-[#EEE4D6] p-3"
                    data-testid="structured-visual-style-reference-v11"
                  >
                    <div className="text-[11px] font-medium text-[#4C392B]">
                      Референс №{Number(reference.order) + 1}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(reference.signals || []).length ? (
                        reference.signals.map((signal) => (
                          <span key={signal} className="rounded-full border border-[#E1D4C2] px-2 py-1 text-[9.5px] text-[#6D5842]">
                            {SIGNAL_LABELS[signal] || signal}
                          </span>
                        ))
                      ) : (
                        <span className="text-[10px] text-[#9A8871]">Роли не отмечены.</span>
                      )}
                    </div>
                    <div className="mt-2 text-[10px] leading-5 text-[#655141]">
                      Заметка: {reference.note || "нет"}
                    </div>
                  </div>
                ))}
              </div>
            </DetailsBlock>

            <DetailsBlock
              title="Технические основания и версии"
              subtitle="Отпечатки, provenance и состояние сборки"
              testid="vs12-details-technical"
            >
              <div className="text-[10.5px] leading-5 text-[#655141]">
                Отпечаток подтверждённых данных: {data.evidence_fingerprint}
                <br />
                Отпечаток визуального стиля: {data.source_fingerprint}
                <br />
                Рассказ: версия {data.source_versions?.story_version || "—"}
                {" · "}Понимание: версия {data.source_versions?.understanding_version || "—"}
                <br />
                VU5.0: {data.source_versions?.vu5_assembler_version || "—"}
                {" · "}VU5.2: {data.source_versions?.vu52_contract_version || "—"}
                <br />
                Неразрешённые конфликты: {data.build_report?.unresolved_conflict_count || 0}
                <br />
                Слабые наблюдения, не повышенные до направления: {data.build_report?.weak_pattern_not_promoted || 0}
                <br />
                Готовность к следующему рабочему сценарию: {data.build_report?.workflow_ready ? "да" : "нет"}
              </div>
            </DetailsBlock>
          </div>
        </div>
      )}
    </section>
  );
}
